import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ContactForm from './ContactForm';
import stylesDefault from '../default.module.css';
import styles from './contactManager.module.css';
import { usePermissionGuard } from '../../adminPanelTools';
import { Icons } from '../../../../code/IconHandler';
import AreaAssignments from './AreaAssignments';
import debounce from 'lodash/debounce';
import { showErrorToast, showInfoToast } from '../../../../code/ToastHelper';

const debouncedSaveAssignment = debounce(
    async (assignmentId, data, fetchAssignments) => {
        try {
            await axios.put(`/api/assignments/${assignmentId}`, data);
            fetchAssignments();
        } catch (err) {
            console.error('Error saving assignment:', err);
        }
    },
    800,
    { leading: false, trailing: true }
);

export default function ContactManager() {
    const checking = usePermissionGuard('handle_contacts');
    const [contacts, setContacts] = useState([]);
    const [contactToEdit, setContactToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [areaAssignments, setAreaAssignments] = useState({});
    const [areaSettings, setAreaSettings] = useState({});
    const EditIcon = Icons.Edit;
    const DeleteIcon = Icons.Delete;

    const fetchAreaSettings = async () => {
        try {
            const res = await axios.get('/api/contact-area-settings');
            const sortedAreas = res.data.sort((a, b) => a.name.localeCompare(b.name));
            const settingsMap = {};
            sortedAreas.forEach(area => {
                settingsMap[area.name] = {
                    id: area.id, // ✅ Include area_id
                    allowMultiple: area.allow_multiple
                };
            });
            setAreaSettings(settingsMap);
        } catch (err) {
            console.error('Error loading area settings:', err);
        }
    };

    const fetchAssignments = async () => {
        try {
            const res = await axios.get('/api/assignments');
            const formatted = {};

            res.data.forEach(item => {
                if (!formatted[item.area_name]) formatted[item.area_name] = [];
                formatted[item.area_name].push({
                    id: item.id, // assignment ID
                    contact: {
                        id: item.contact_id,
                        first_name: item.first_name,
                        family_name: item.family_name,
                        tel: item.tel,
                        email: item.email,
                        img: item.img
                    },
                    position: item.position || '',
                    aufgabe: item.aufgabe || '',
                    show_email: item.show_email ?? false,
                    show_phone: item.show_phone ?? false
                });
            });

            setAreaAssignments(formatted);
        } catch (err) {
            console.error('Error loading assignments:', err);
        }
    };

    const fetchContacts = async () => {
        try {
            const res = await axios.get('/api/contact/');
            setContacts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error loading contacts:', err);
        }
    };
    useEffect(() => {
        fetchContacts();
        fetchAreaSettings();
        fetchAssignments();
    }, []);
    const deleteContact = async (id) => {
        if (!window.confirm('Möchtest du diesen Kontakt wirklich löschen?')) return;
        try {
            await axios.delete(`/api/contact/${id}`);
            await fetchContacts();
        } catch (err) {
            console.error('Fehler beim Löschen des Kontakts:', err);
            showErrorToast('Fehler beim Löschen des Kontakts.');
        }
    };

    const resolveAreaIdByName = (name) => {
        return areaSettings[name]?.id ?? null;
    };

    const addContactToArea = async (area, contact) => {
        try {
            const areaId = resolveAreaIdByName(area); // Add this helper below
            const res = await axios.post('/api/assignments', {
                area_id: areaId,
                contact_id: contact.id,
                show_email: true,
                show_phone: true
            });

            const assignment = res.data;
            setAreaAssignments(prev => ({
                ...prev,
                [area]: [...(prev[area] || []), {
                    id: assignment.id,
                    contact,
                    position: assignment.position || '',
                    aufgabe: assignment.aufgabe || '',
                    show_email: assignment.show_email ?? true,
                    show_phone: assignment.show_phone ?? true
                }]
            }));
        } catch (err) {
            console.error('Error assigning contact:', err);
        }
    };

    const updatePosition = (area, contactId, newPosition) => {
        // 1. Update UI state immediately
        setAreaAssignments(prev => {
            const updated = { ...prev };
            updated[area] = updated[area].map(a =>
                a.contact.id === contactId ? { ...a, position: newPosition } : a
            );
            return updated;
        });

        // 2. Debounced backend update
        const assignment = areaAssignments[area]?.find(a => a.contact.id === contactId);
        if (!assignment) return;

        const payload = {
            area_id: resolveAreaIdByName(area),
            contact_id: contactId,
            position: newPosition,
            aufgabe: assignment.aufgabe,
            show_email: assignment.show_email,
            show_phone: assignment.show_phone
        };

        debouncedSaveAssignment(assignment.id, payload, fetchAssignments);
    };
    const updateAufgabe = (area, contactId, newAufgabe) => {
        // 1. Update UI state immediately
        setAreaAssignments(prev => {
            const updated = { ...prev };
            updated[area] = updated[area].map(a =>
                a.contact.id === contactId ? { ...a, aufgabe: newAufgabe } : a
            );
            return updated;
        });

        // 2. Debounced backend update
        const assignment = areaAssignments[area]?.find(a => a.contact.id === contactId);
        if (!assignment) return;

        const payload = {
            area_id: resolveAreaIdByName(area),
            contact_id: contactId,
            position: assignment.position,
            aufgabe: newAufgabe,
            show_email: assignment.show_email,
            show_phone: assignment.show_phone
        };

        debouncedSaveAssignment(assignment.id, payload, fetchAssignments);
    };

    const removeFromArea = async (area, contactId) => {
        const assignment = areaAssignments[area]?.find(a => a.contact.id === contactId);
        if (!assignment) return;

        try {
            await axios.delete(`/api/assignments/${assignment.id}`);
            fetchAssignments();
        } catch (err) {
            console.error('Error removing contact from area:', err);
        }
    };

    const toggleEmailVisibility = async (area, contactId) => {
        setAreaAssignments(prev => {
            const updated = prev[area].map(entry => {
                if (entry.contact.id !== contactId) return entry;

                const updatedEntry = { ...entry, show_email: !entry.show_email };

                // Persist change to backend
                axios.put(`/api/assignments/${entry.id}`, {
                    area_id: resolveAreaIdByName(area),
                    contact_id: contactId,
                    position: updatedEntry.position,
                    aufgabe: updatedEntry.aufgabe,
                    show_email: updatedEntry.show_email,
                    show_phone: updatedEntry.show_phone
                }).catch(err => {
                    console.error("Error updating email visibility:", err);
                    showInfoToast("Fehler beim Speichern der E-Mail-Sichtbarkeit.");
                });

                return updatedEntry;
            });

            return { ...prev, [area]: updated };
        });
    };

    const togglePhoneVisibility = async (area, contactId) => {
        setAreaAssignments(prev => {
            const updated = prev[area].map(entry => {
                if (entry.contact.id !== contactId) return entry;

                const updatedEntry = { ...entry, show_phone: !entry.show_phone };

                // Persist change to backend
                axios.put(`/api/assignments/${entry.id}`, {
                    area_id: resolveAreaIdByName(area),
                    contact_id: contactId,
                    position: updatedEntry.position,
                    aufgabe: updatedEntry.aufgabe,
                    show_email: updatedEntry.show_email,
                    show_phone: updatedEntry.show_phone
                }).catch(err => {
                    console.error("Error updating phone visibility:", err);

                    showInfoToast("Fehler beim Speichern der Telefonnummernsichtbarkeit.");
                });

                return updatedEntry;
            });

            return { ...prev, [area]: updated };
        });
    };

    if (checking) return <div className={styles.container}><p>Lade...</p></div>;

    return (
        <div className={stylesDefault.container}>
            <h1>Kontakte verwalten</h1>

            <button
                className={stylesDefault.blueButton}
                onClick={() => setContactToEdit({})}
            >
                + Neuen Kontakt anlegen
            </button>

            {contactToEdit !== null && (
                <ContactForm
                    contact={contactToEdit}
                    onSave={async (id, action) => {
                        await fetchContacts();
                        setContactToEdit(null);
                        showInfoToast(`Kontakt wurde ${action === 'create' ? 'erstellt' : 'aktualisiert'}!`)
                    }}
                    onClose={() => setContactToEdit(null)}
                />
            )}
            <input
                type="text"
                placeholder="Kontakte durchsuchen..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className={stylesDefault.searchInput}
            />
            <div className={styles.contactsList}>
                {contacts.length === 0 && <p>Keine Kontakte gefunden.</p>}
                {contacts
                    .filter(c => {
                        const fullName = `${c.first_name} ${c.family_name}`.toLowerCase();
                        return fullName.includes(searchTerm.toLowerCase());
                    })
                    .map(c => (
                        <div key={c.id} className={styles.contactRow}>
                            <div className={styles.contactInfo}>
                                <div className={styles.imageWrapper}>
                                    <img
                                        src={c.img
                                            ? `${process.env.PUBLIC_URL}/uploads/personen/${c.img}`
                                            : `${process.env.PUBLIC_URL}/uploads/personen/default_person.svg`}
                                        alt={`${c.first_name} ${c.family_name}`}
                                        style={{ height: '50px', borderRadius: '50%' }}
                                    />
                                </div>
                                <div className={styles.contactDetails}>
                                    <div className={styles.contactName}>
                                        {c.first_name} {c.family_name}
                                    </div>
                                    <div className={styles.contactMeta}>
                                        <span>{c.tel || "-"}</span>
                                        {c.email && (
                                            <>
                                                <span> | </span>
                                                <a href={`mailto:${c.email}`} className={styles.contactEmail}>
                                                    {c.email}
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.contactActions}>
                                <button
                                    className={stylesDefault.greenButton}
                                    onClick={() => setContactToEdit(c)}
                                    aria-label={`Kontakt ${c.first_name} ${c.family_name} bearbeiten`}
                                >
                                    <EditIcon />
                                </button>
                                <button
                                    className={stylesDefault.redButton}
                                    onClick={() => deleteContact(c.id)}
                                    aria-label={`Kontakt ${c.first_name} ${c.family_name} löschen`}
                                >
                                    <DeleteIcon />
                                </button>
                            </div>
                        </div>
                    ))}
            </div>
            <div className={styles.assignements}>
                <AreaAssignments
                    contacts={contacts}
                    assignments={areaAssignments}
                    areaSettings={areaSettings}
                    onAddContact={addContactToArea}
                    onRemoveContact={removeFromArea}
                    onUpdatePosition={updatePosition}
                    onUpdateAufgabe={updateAufgabe}
                    onToggleEmail={toggleEmailVisibility}
                    onTogglePhone={togglePhoneVisibility}
                />
            </div>
        </div>
    );
}
